import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate } from "framer-motion";
import VialImage from "@/components/VialImage";

const SPRING = { stiffness: 150, damping: 20, mass: 0.5 };

export default function HeroVialCard({ product }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), SPRING);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), SPRING);
  const glowX = useSpring(useTransform(mx, [-0.5, 0.5], [20, 80]), SPRING);
  const glowY = useSpring(useTransform(my, [-0.5, 0.5], [10, 70]), SPRING);
  const glow = useMotionTemplate`radial-gradient(circle at ${glowX}% ${glowY}%, rgba(14,165,233,0.14), transparent 55%)`;

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{ perspective: 1200 }} className="relative mx-auto w-full max-w-md lg:max-w-none">
      <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-vial-card"
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)] overflow-visible cursor-default">
        <motion.div style={{ backgroundImage: glow }} className="absolute inset-0 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none opacity-[0.35]"
          style={{ backgroundImage: "linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative flex items-center justify-center py-10 sm:py-14 px-6">
          {product ? <VialImage product={product} className="h-[380px] sm:h-[440px] drop-shadow-xl" /> : <div className="h-[440px] w-56 bg-slate-100 animate-pulse" />}
        </div>
        <motion.div style={{ translateZ: 30 }} data-testid="hero-live-coa-pill"
          className="absolute top-4 right-4 inline-flex items-center gap-2 border border-slate-200 bg-white/95 backdrop-blur px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-700">
            {product?.coa_lot ? `Lot ${product.coa_lot}` : "Lot pending"}{product?.coa_purity ? ` · ${product.coa_purity}%` : ""}
          </span>
        </motion.div>
        <motion.div style={{ translateZ: 45 }} data-testid="hero-reference-chip"
          className="absolute -bottom-6 -left-3 sm:-left-8 bg-[#0B111A] text-white border border-slate-700 px-5 py-4 shadow-lg min-w-[200px]">
          <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">Reference SKU</p>
          <p className="font-display text-xl font-semibold mt-1">{product?.name || "—"}</p>
          <p className="font-tech text-[10px] uppercase tracking-[0.16em] text-slate-400 mt-0.5">
            {product ? `${product.alias || product.category} · ${product.specification.split(" ")[0]}` : ""}
          </p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
